import React, {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
} from 'react'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'

const MAX_OPEN_TEAM_CHATS = 3

type OpenTeamChatsContextType = {
    openTeamChatIds: string[]
    openTeamChat: (chatId: string) => void
    closeTeamChat: (chatId: string) => void
    isTeamChatOpen: (chatId: string) => boolean
    minimizedTeamChatIds: string[]
    toggleMinimizeTeamChat: (chatId: string) => void
    isTeamChatMinimized: (chatId: string) => boolean
}

const OpenTeamChatsContext = createContext<OpenTeamChatsContextType>({
    openTeamChatIds: [],
    openTeamChat: () => {},
    closeTeamChat: () => {},
    isTeamChatOpen: () => false,
    minimizedTeamChatIds: [],
    toggleMinimizeTeamChat: () => {},
    isTeamChatMinimized: () => false,
})

/**
 * Tracks popped-out team chat windows. Same persistence model as client-chat
 * popovers so they survive navigation and reloads.
 */
export default function OpenTeamChatsProvider({
    children,
}: PropsWithChildren) {
    const [openTeamChatIds, setOpenTeamChatIds] = useLocalStorage<string[]>(
        LocalStorageKeys.openTeamChatPopoverIds,
        [],
    )
    const [minimizedTeamChatIds, setMinimizedTeamChatIds] = useLocalStorage<
        string[]
    >(LocalStorageKeys.minimizedTeamChatPopoverIds, [])

    const openTeamChat = useCallback(
        (chatId: string) => {
            setOpenTeamChatIds(prev => {
                const without = prev.filter(id => id !== chatId)
                return [chatId, ...without].slice(0, MAX_OPEN_TEAM_CHATS)
            })
            setMinimizedTeamChatIds(prev => prev.filter(id => id !== chatId))
        },
        [setOpenTeamChatIds, setMinimizedTeamChatIds],
    )

    const closeTeamChat = useCallback(
        (chatId: string) => {
            setOpenTeamChatIds(prev => prev.filter(id => id !== chatId))
            setMinimizedTeamChatIds(prev => prev.filter(id => id !== chatId))
        },
        [setOpenTeamChatIds, setMinimizedTeamChatIds],
    )

    const toggleMinimizeTeamChat = useCallback(
        (chatId: string) => {
            setMinimizedTeamChatIds(prev =>
                prev.includes(chatId)
                    ? prev.filter(id => id !== chatId)
                    : [...prev, chatId],
            )
        },
        [setMinimizedTeamChatIds],
    )

    const isTeamChatOpen = useCallback(
        (chatId: string) => openTeamChatIds.includes(chatId),
        [openTeamChatIds],
    )

    const isTeamChatMinimized = useCallback(
        (chatId: string) => minimizedTeamChatIds.includes(chatId),
        [minimizedTeamChatIds],
    )

    return (
        <OpenTeamChatsContext.Provider
            value={{
                openTeamChatIds,
                openTeamChat,
                closeTeamChat,
                isTeamChatOpen,
                minimizedTeamChatIds,
                toggleMinimizeTeamChat,
                isTeamChatMinimized,
            }}
        >
            {children}
        </OpenTeamChatsContext.Provider>
    )
}

export function useOpenTeamChats() {
    return useContext(OpenTeamChatsContext)
}
